// Section (g): somewhere you already have in mind.
//
// Distinct from the location features above it: those say what you want nearby, this names
// an actual place. Writes a `region` row rather than a household column, so it owns its own
// save instead of going through the intake debounce.

import { useState, type JSX } from 'react'

import { geocode } from '../../lib/geoClient'
import { findNearDuplicate, previewCandidates, type PlacePoint } from '../../lib/nearDuplicate'

export interface SuggestPlaceFieldProps {
  /** Every candidate already on the trip, LLM-proposed and suggested alike. */
  candidates: readonly PlacePoint[]
  /** Ids of the candidates this household suggested, so they can be retracted. */
  ownIds: readonly string[]
  disabled?: boolean
  onSuggest: (place: { name: string; lat: number; lng: number }) => Promise<void>
  onRetract: (regionId: string) => Promise<void>
}

type Status =
  | { kind: 'idle' }
  | { kind: 'working' }
  | { kind: 'error'; message: string }
  | { kind: 'confirm'; place: { name: string; lat: number; lng: number }; duplicate: PlacePoint }

export function SuggestPlaceField({
  candidates,
  ownIds,
  disabled = false,
  onSuggest,
  onRetract,
}: SuggestPlaceFieldProps): JSX.Element {
  const [draft, setDraft] = useState('')
  const [status, setStatus] = useState<Status>({ kind: 'idle' })
  const [showAll, setShowAll] = useState(false)

  const own = new Set(ownIds)
  const { shown, hidden } = previewCandidates(candidates, ownIds, showAll)

  async function submit(place: { name: string; lat: number; lng: number }): Promise<void> {
    setStatus({ kind: 'working' })
    try {
      await onSuggest(place)
      setDraft('')
      setStatus({ kind: 'idle' })
    } catch {
      setStatus({ kind: 'error', message: 'Could not save that suggestion. Try again.' })
    }
  }

  async function lookup(): Promise<void> {
    const name = draft.trim()
    if (!name) return
    setStatus({ kind: 'working' })

    let found: Awaited<ReturnType<typeof geocode>>
    try {
      found = await geocode(name)
    } catch {
      setStatus({ kind: 'error', message: 'Could not look that place up. Try again.' })
      return
    }
    if (!found) {
      setStatus({ kind: 'error', message: `We couldn't find "${name}". Try adding a state.` })
      return
    }

    const place = { name, lat: found.lat, lng: found.lng }
    const duplicate = findNearDuplicate(place, candidates)
    if (duplicate) {
      setStatus({ kind: 'confirm', place, duplicate })
      return
    }
    await submit(place)
  }

  return (
    <div>
      <p className="spz-hint">
        Been somewhere that worked, or know a place the rest of us don&rsquo;t? Name it. It gets
        scored on everyone&rsquo;s travel exactly like the ones the app suggests.
      </p>

      <label className="spz-field">
        <span className="spz-label">A place</span>
        <input
          className="spz-input"
          type="text"
          placeholder="e.g. Deep Creek Lake, MD"
          value={draft}
          disabled={disabled || status.kind === 'working'}
          onChange={(e) => {
            setDraft(e.target.value)
            if (status.kind === 'error' || status.kind === 'confirm') setStatus({ kind: 'idle' })
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void lookup()
            }
          }}
        />
      </label>

      <button
        type="button"
        className="spz-btn"
        disabled={disabled || status.kind === 'working' || draft.trim().length === 0}
        onClick={() => void lookup()}
      >
        {status.kind === 'working' ? 'Adding…' : 'Suggest this place'}
      </button>

      {status.kind === 'error' && (
        <p className="spz-error" role="alert">
          {status.message}
        </p>
      )}

      {status.kind === 'confirm' && (
        <div className="spz-confirm" role="alert">
          <p>
            <strong>{status.duplicate.name}</strong> is already on the list, and it&rsquo;s
            practically the same spot. Add yours anyway?
          </p>
          <button type="button" className="spz-btn" onClick={() => void submit(status.place)}>
            Add it anyway
          </button>
          <button
            type="button"
            className="spz-btn spz-btn-ghost"
            onClick={() => {
              setDraft('')
              setStatus({ kind: 'idle' })
            }}
          >
            Never mind
          </button>
        </div>
      )}

      <div className="spz-candidates">
        <span className="spz-label">Already being considered</span>
        {candidates.length === 0 ? (
          <p className="spz-hint">Nothing yet — yours would be the first.</p>
        ) : (
          <ul>
            {shown.map((c) => (
              <li key={c.id}>
                {c.name}
                {own.has(c.id) && (
                  <button
                    type="button"
                    className="spz-retract"
                    disabled={disabled}
                    onClick={() => void onRetract(c.id)}
                    aria-label={`Remove ${c.name}`}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {hidden > 0 && (
          <button
            type="button"
            className="spz-more"
            aria-expanded={showAll}
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? 'Show fewer' : `Show all ${candidates.length}`}
          </button>
        )}
      </div>
    </div>
  )
}
